import React from 'react';
import {
  MdChevronRight, MdPerson, MdSettings, MdSmartphone, MdPalette,
  MdShield, MdNotifications, MdLanguage, MdHelpCenter, MdMic,
  MdDashboard, MdTrendingUp, MdPhone, MdPhoneInTalk, MdChat,
  MdCreditCard, MdLock, MdVpnKey,
} from 'react-icons/md';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { useProfile } from '../contexts/ProfileContext';
import { useApp } from '../contexts/AppContext';
import { computeBudgetRule } from '../utils/computeBudgetRule';

ChartJS.register(ArcElement, Tooltip);

const ProfileScreen = ({ onNavigate }) => {
  const { loadedProfileData } = useProfile();
  const { selectedBackend, setSelectedBackend, selectedModel, setSelectedModel, theme, setTheme, currentUser, isAdmin, openChangePassword, ollamaNoThink, setOllamaNoThink } = useApp();

  const p        = loadedProfileData?.profile;
  const customer = p?.customer || {};

  const name = customer.first_name && customer.last_name
    ? `${customer.first_name} ${customer.last_name}`
    : loadedProfileData?.name || (isAdmin ? 'Administrator' : currentUser || 'Guest User');
  const initials  = name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const subtitle  = [customer.employment?.occupation, customer.address?.city && customer.address?.province ? `${customer.address.city}, ${customer.address.province}` : customer.address?.city].filter(Boolean).join(' · ');
  const email     = customer.email || null;
  const memberSince = customer.member_since || customer.relationship_start || null;

  const fmtCad = v => v != null ? `$${Math.abs(Number(v)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

  const budgetStats  = p ? computeBudgetRule(p) : null;
  const avgIncome    = budgetStats?.avgIncome ?? null;
  const avgNeeds     = budgetStats?.avgNeeds ?? 0;
  const avgSavings   = budgetStats?.avgSavings ?? 0;
  const avgDebt      = budgetStats?.avgDebt ?? 0;
  const avgDisc      = budgetStats?.avgDisc ?? 0;
  const avgExpenses  = budgetStats ? avgNeeds + avgDisc + avgDebt : null;
  const surplus      = avgIncome != null && avgExpenses != null ? avgIncome - avgExpenses : null;
  const utilPct      = avgIncome > 0 ? Math.min(100, Math.round((avgExpenses / avgIncome) * 100)) : 0;

  const SPEND_COLORS = ['#f59e0b', '#008a3e', '#1976d2', '#7c3aed'];
  const donutData = {
    labels: ['Needs', 'Savings', 'Discretionary', 'Debt & Giving'],
    datasets: [{
      data: [avgNeeds, avgSavings, avgDisc, avgDebt],
      backgroundColor: SPEND_COLORS,
      borderColor: 'transparent',
      borderWidth: 0,
    }],
  };
  const donutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ${ctx.label}: $${ctx.raw.toLocaleString()}` } } },
  };
  const legendItems = [
    { color: SPEND_COLORS[0], label: 'Needs',         val: avgNeeds },
    { color: SPEND_COLORS[1], label: 'Savings',       val: avgSavings },
    { color: SPEND_COLORS[2], label: 'Discretionary', val: avgDisc },
    { color: SPEND_COLORS[3], label: 'Debt & Giving', val: avgDebt },
  ].filter(item => item.val > 0);

  const BACKENDS = [['cohere','Cohere','#008a3e'],['openrouter','Router','#7c3aed'],['claude','Claude','#d97706'],['ollama','Ollama','#555']];

  const SettingsRow = ({ icon: Icon, label, value, onClick, children }) => (
    <div className="profile-settings-row" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="profile-settings-row-left">
        <span className="profile-settings-icon"><Icon size={17} /></span>
        <span className="profile-settings-label">{label}</span>
      </div>
      <div className="profile-settings-row-right">
        {children || (value && <span className="profile-settings-value">{value}</span>)}
        {onClick && <MdChevronRight size={18} style={{ color: 'var(--text-light)', flexShrink: 0 }} />}
      </div>
    </div>
  );

  return (
    <div className="screen">
      <div className="profile-hero">
        <div className="profile-hero-actions">
          {currentUser && openChangePassword && (
            <button
              type="button"
              className="profile-hero-action"
              onClick={openChangePassword}
              title="Change password"
              aria-label="Change password"
            >
              <MdVpnKey size={20} />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="profile-hero-action"
              onClick={() => onNavigate('admin')}
              title="Approve accounts"
              aria-label="Approve accounts"
            >
              <MdSettings size={20} />
            </button>
          )}
        </div>
        <div className="profile-avatar-ring">
          <div className="profile-avatar">{initials || <MdPerson size={28} />}</div>
        </div>
        <div className="profile-hero-name">{name}</div>
        {subtitle && <div className="profile-hero-sub">{subtitle}</div>}
        {email && <div className="profile-hero-email">{email}</div>}
        {memberSince && <div className="profile-hero-since">Member since {memberSince}</div>}
      </div>

      <div className="scroll-content gray-bg">

        {p && avgIncome != null && avgExpenses != null && (
          <div className="profile-section">
            <div className="profile-section-title">Financial Snapshot</div>
            <div className="profile-card" style={{ padding: '14px 16px' }}>

              {/* Doughnut + legend */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 100, height: 100, flexShrink: 0 }}>
                  <Doughnut data={donutData} options={donutOpts} />
                </div>
                <div style={{ flex: 1 }}>
                  {legendItems.map(({ color, label, val }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: 'var(--text-medium)' }}>{label}</span>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark)' }}>${val.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stat tiles */}
              <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                {[
                  { label: 'Income',   val: `+${fmtCad(avgIncome)}`,                                                                              color: '#1b5e20' },
                  { label: 'Expenses', val: `–${fmtCad(avgExpenses)}`,                                                                            color: '#b71c1c' },
                  { label: 'Surplus',  val: `${surplus >= 0 ? '+' : '–'}${fmtCad(Math.abs(surplus))}`,  color: surplus >= 0 ? '#1b5e20' : '#b71c1c' },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ flex: 1, textAlign: 'center', background: 'var(--bg-main)', borderRadius: 8, padding: '8px 4px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-light)', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Utilization bar */}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-light)', marginBottom: 5 }}>
                  <span>Spending utilization</span>
                  <span>{utilPct}% of income</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'rgba(128,128,128,0.15)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${utilPct}%`, borderRadius: 3, background: surplus >= 0 ? 'var(--td-green)' : '#b71c1c', transition: 'width 0.5s' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="profile-section">
          <div className="profile-section-title">AI & Backend</div>
          <div className="profile-card">
            <SettingsRow icon={MdSmartphone} label="Active Backend">
              <div className="profile-backend-chips">
                {BACKENDS.map(([val, label, color]) => (
                  <button key={val}
                    className={`profile-chip${selectedBackend === val ? ' active' : ''}`}
                    style={{ '--chip-color': color }}
                    onClick={() => setSelectedBackend(val)}>
                    {label}
                  </button>
                ))}
              </div>
            </SettingsRow>
            <SettingsRow icon={MdSettings} label="OpenRouter Model"
              value={selectedModel.split('/').pop().replace(':free', '')} />
            {selectedBackend === 'ollama' && (
              <SettingsRow icon={MdSmartphone} label={ollamaNoThink ? 'Ollama: Fast (No Think)' : 'Ollama: Think mode'}>
                <button
                  className={`profile-chip${ollamaNoThink ? ' active' : ''}`}
                  style={{ '--chip-color': ollamaNoThink ? '#f59e0b' : '#7c3aed' }}
                  onClick={() => setOllamaNoThink(!ollamaNoThink)}>
                  {ollamaNoThink ? 'No Think' : 'Think'}
                </button>
              </SettingsRow>
            )}
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section-title">Appearance</div>
          <div className="profile-card">
            <SettingsRow icon={MdPalette} label="Theme">
              <div className="profile-backend-chips">
                <button
                  className={`profile-chip${theme === 'green' ? ' active' : ''}`}
                  style={{ '--chip-color': '#008a3e' }}
                  onClick={() => setTheme('green')}>
                  ☀️ Light
                </button>
                <button
                  className={`profile-chip${theme === 'purple' ? ' active' : ''}`}
                  style={{ '--chip-color': '#6A55F7' }}
                  onClick={() => setTheme('purple')}>
                  🌙 Dark
                </button>
              </div>
            </SettingsRow>
            <SettingsRow icon={MdLanguage} label="Language" value="English (CA)" />
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section-title">Account & Security</div>
          <div className="profile-card">
            <SettingsRow icon={MdShield}        label="Security Settings"  onClick={() => onNavigate('security')} />
            <SettingsRow icon={MdNotifications} label="Notifications"      onClick={() => {}} />
            <SettingsRow icon={MdHelpCenter}    label="Help & Support"     onClick={() => {}} />
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section-title">My Cards</div>
          <div className="profile-card">
            <SettingsRow icon={MdCreditCard} label="Manage Cards"          onClick={() => onNavigate('cardMgmt')} />
            <SettingsRow icon={MdLock}       label="Lock / Freeze a Card"  onClick={() => onNavigate('cardMgmt')} />
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section-title">Contact TD</div>
          <div className="profile-card">
            <div className="profile-contact-cta" onClick={() => onNavigate('call')}>
              <div className="profile-contact-icon-wrap">
                <MdPhoneInTalk size={24} />
              </div>
              <div className="profile-contact-text">
                <div className="profile-contact-label">Call TD Anytime</div>
                <div className="profile-contact-sub">1-866-222-3456 · 24/7 support</div>
              </div>
              <MdChevronRight size={20} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
            </div>
            <SettingsRow icon={MdPhone} label="TD EasyLine" value="1-866-222-3456" />
            <SettingsRow icon={MdChat}  label="In-App Chat" onClick={() => onNavigate('chat')} />
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section-title">Demo Tools</div>
          <div className="profile-card">
            <SettingsRow icon={MdMic}        label="Voice Assistant"     onClick={() => onNavigate('voiceBot')} />
            <SettingsRow icon={MdDashboard}  label="Financial Dashboard" onClick={() => onNavigate('financialSummary')} />
            <SettingsRow icon={MdTrendingUp} label="70/20/10 Budget"     onClick={() => onNavigate('budgetRule')} />
          </div>
        </div>

        <div style={{ height: 16 }} />
      </div>
    </div>
  );
};

export default ProfileScreen;
